import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Flame, Car, Swords, Cable } from 'lucide-react';
import { Button } from '../components/ui/button';

const HomePage = () => {
  const services = [
    {
      icon: Swords,
      title: 'Cascades Physiques',
      description: 'Combats, chutes, réactions aux impacts. Chorégraphie de combats réalistes pour le cinéma et la télévision.',
      features: ['Combats à mains nues', 'Arts martiaux', 'Escrime', 'Bagarres de groupe'],
    },
    {
      icon: Car,
      title: 'Mécaniques',
      description: 'Auto, moto, bateau, travelling. Poursuites, crash, retournements et collisions contrôlées.',
      features: ['Poursuites auto/moto', 'Crash contrôlés', 'Travelling action', 'Cascades bateau'],
    },
    {
      icon: Flame,
      title: 'Torche & Pyrotechnie',
      description: 'Body burns, torches humaines, explosions contrôlées, effets de feu et impacts.',
      features: ['Torche complète', 'Body burns', 'Explosions', 'Impacts de balles'],
    },
    {
      icon: Cable,
      title: 'Chutes & Câblage',
      description: 'Chutes de hauteur, vols, projections et acrobaties aériennes sur câbles.',
      features: ['Chutes de hauteur', 'Vols aériens', 'Projections', 'Suspensions'],
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1510177087648-3ae3fa149848?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxzdHVudG1hbiUyMGZpcmUlMjBtb3RvcmN5Y2xlJTIwanVtcCUyMGV4cGxvc2lvbiUyMHBhcmtvdXIlMjBhY3Rpb258ZW58MHx8fHwxNzcyNzEzNzEyfDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 
              className="font-['Teko'] text-7xl md:text-9xl lg:text-[12rem] font-semibold text-[#FACC15] tracking-tighter leading-none mb-4"
              data-testid="hero-title"
            >
              STUNT DIRECTOR
            </h1>
            <p className="font-['Teko'] text-2xl md:text-4xl text-white/90 tracking-widest uppercase mb-4">
              Coordination & Réglage de Cascades
            </p>
            <p className="font-['Barlow'] text-base md:text-lg text-[#FACC15] max-w-3xl mx-auto mb-8 tracking-wide">
              Cascade physique · Mécanique auto / moto / bateau · Travelling · Chute de hauteur · Torche · Câblage · Réalisation en 2ème équipe action
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services">
                <Button 
                  className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-8 py-6 text-lg hover:bg-[#EAB308] hover:scale-105 transition-all duration-200 skew-x-[-12deg] shadow-[0_0_20px_rgba(250,204,21,0.3)]"
                  data-testid="hero-cta-devis"
                >
                  <span className="skew-x-[12deg] flex items-center gap-2">
                    Demander un devis <ChevronRight size={20} />
                  </span>
                </Button>
              </Link>
              <Link to="/gallery">
                <Button 
                  variant="outline"
                  className="border-[#FACC15] text-[#FACC15] font-bold uppercase tracking-widest px-8 py-6 text-lg hover:bg-[#FACC15] hover:text-black transition-all duration-200 skew-x-[-12deg]"
                  data-testid="hero-cta-gallery"
                >
                  <span className="skew-x-[12deg]">Voir nos réalisations</span>
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1.5 h-3 bg-[#FACC15] rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-black">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Nos expertises
            </p>
            <h2 className="font-['Teko'] text-5xl md:text-7xl text-white">
              SERVICES PROFESSIONNELS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative bg-[#09090b] border border-white/10 p-8 hover:border-[#FACC15]/50 transition-all duration-300"
                data-testid={`service-card-${service.title.toLowerCase()}`}
              >
                <div className="absolute top-0 left-0 w-1 h-0 bg-[#FACC15] group-hover:h-full transition-all duration-300" />
                <service.icon className="w-12 h-12 text-[#FACC15] mb-6" />
                <h3 className="font-['Teko'] text-2xl text-white uppercase mb-3">
                  {service.title}
                </h3>
                <p className="font-['Barlow'] text-gray-400 leading-relaxed mb-4">
                  {service.description}
                </p>
                <ul className="space-y-1">
                  {service.features.map((feature) => (
                    <li key={feature} className="font-['Barlow'] text-gray-500 text-sm flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#FACC15]"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="ghost" 
                className="text-[#FACC15] hover:text-white uppercase tracking-wider font-semibold"
                data-testid="services-more-btn"
              >
                En savoir plus <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 bg-[#09090b]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Notre équipe
            </p>
            <h2 className="font-['Teko'] text-5xl md:text-7xl text-white">
              PROFESSIONNELS DU STUNT
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Alexandra CHALLET',
                role: 'Scénariste, Réalisatrice d\'action & documentaire',
                specialty: '2ème équipe action, Actrice',
                phone: '06 29 65 35 24',
                photo: '/api/files/2c7fba2d-d914-4abe-ac41-cbacafaf8f6a',
              },
              {
                name: 'André FERRER',
                role: 'Comédien cascadeur',
                specialty: 'Régleur cascade',
                phone: '07 68 25 76 00',
                photo: '/api/files/45653338-8dc4-4fad-848b-46a31af96957',
              },
              {
                name: 'Éric TATIN',
                role: 'Régleur cascade',
                specialty: 'Spécialisé câblage',
                phone: '07 86 12 39 12',
                photo: '/api/files/d6c13e23-3580-49b2-9c45-45a57ef5c2fb',
              },
            ].map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#18181b] border border-white/10 p-8 text-center hover:border-[#FACC15]/30 transition-colors"
                data-testid={`team-member-${index}`}
              >
                <div className="w-32 h-32 mx-auto mb-6 rounded-sm overflow-hidden">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${member.photo}`}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-['Teko'] text-2xl text-white uppercase mb-2">
                  {member.name}
                </h3>
                <p className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mb-1">
                  {member.role}
                </p>
                <p className="font-['Barlow'] text-gray-400 text-sm mb-4">
                  {member.specialty}
                </p>
                <a 
                  href={`tel:${member.phone.replace(/\s/g, '')}`}
                  className="font-['Barlow'] text-white hover:text-[#FACC15] transition-colors"
                >
                  {member.phone}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-black relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1581481461675-4d0868688ad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxzdHVudG1hbiUyMGZpcmUlMjBtb3RvcmN5Y2xlJTIwanVtcCUyMGV4cGxvc2lvbiUyMHBhcmtvdXIlMjBhY3Rpb258ZW58MHx8fHwxNzcyNzEzNzEyfDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
                Prêt à tourner ?
              </p>
              <h2 className="font-['Teko'] text-5xl md:text-7xl text-white mb-6">
                CONCRÉTISEZ VOTRE PROJET
              </h2>
              <p className="font-['Barlow'] text-gray-300 text-lg mb-8 leading-relaxed">
                Vous avez un projet de film, série ou publicité nécessitant des cascades ? 
                Contactez-nous pour un devis personnalisé adapté à vos besoins.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services">
                  <Button 
                    className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-8 py-6 hover:bg-[#EAB308] transition-all duration-200"
                    data-testid="cta-devis-btn"
                  >
                    Demander un devis
                  </Button>
                </Link>
                <Link to="/trainings">
                  <Button 
                    variant="outline"
                    className="border-white/30 text-white font-bold uppercase tracking-widest px-8 py-6 hover:bg-white/10 transition-all duration-200"
                    data-testid="cta-formations-btn"
                  >
                    Nos formations
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Radio Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12"
          >
            <Link to="/radio" className="group">
              <div className="relative">
                <div className="absolute inset-0 bg-[#FACC15]/20 blur-2xl rounded-full group-hover:bg-[#FACC15]/30 transition-colors duration-300" />
                <img 
                  src="/images/stunt-radio-logo.jpg"
                  alt="Stunt Radio"
                  className="w-40 h-40 md:w-48 md:h-48 object-contain relative z-10 group-hover:scale-105 transition-transform duration-300"
                  data-testid="radio-logo"
                />
              </div>
            </Link>
            <div className="text-center md:text-left">
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-2">
                Écoutez-nous en direct
              </p>
              <h3 className="font-['Teko'] text-4xl md:text-5xl text-white mb-3">
                STUNT RADIO
              </h3>
              <p className="font-['Barlow'] text-gray-400 text-sm mb-4">
                ON AIR · ON RIDE · ON FIRE
              </p>
              <Link to="/radio">
                <Button 
                  className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-6 py-4 hover:bg-[#EAB308] transition-all duration-200"
                  data-testid="radio-cta-btn"
                >
                  Écouter la radio
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
